import { Button } from "zudoku/components";
import { Badge } from "zudoku/ui/Badge";
import {
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "zudoku/ui/Accordion";
import { Alert, AlertDescription, AlertTitle } from "zudoku/ui/Alert";
import { Card } from "zudoku/ui/Card";
import { Skeleton } from "zudoku/ui/Skeleton";
import { KeyRound, Pencil, Plus, RefreshCw, Trash2 } from "zudoku/icons";
import type { ApiKey, OrgApp } from "../types/apiManagement";

interface AppCardProps {
	app: OrgApp;
	keys: ApiKey[];
	onCreateKey: (app: OrgApp) => void;
	onEditApp: (app: OrgApp) => void;
	onDeleteApp: (app: OrgApp) => void;
	onDeleteKey: (app: OrgApp, key: ApiKey) => void;
	onRefreshKeys: (app: OrgApp) => void;
	isLoadingKeys?: boolean;
	keysError?: string | null;
}

const formatDate = (value?: string) => {
	if (!value) return "—";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return value;
	}
	return date.toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
};

export const AppCard = ({
	app,
	keys,
	onCreateKey,
	onEditApp,
	onDeleteApp,
	onDeleteKey,
	onRefreshKeys,
	isLoadingKeys,
	keysError,
}: AppCardProps) => {
	return (
		<AccordionItem value={app.app_id} className="border-none">
			<Card className="overflow-hidden border border-border">
				<AccordionTrigger className="flex items-start gap-4 px-6 py-4 hover:no-underline">
					<div className="flex flex-1 flex-col text-left">
						<div className="flex flex-wrap items-center gap-3">
							<h3 className="text-lg font-semibold">{app.name}</h3>
							<Badge variant="secondary" className="flex items-center gap-1">
								<KeyRound size={14} />
								{keys.length} {keys.length === 1 ? "key" : "keys"}
							</Badge>
						</div>
						{app.description ? (
							<p className="mt-2 text-sm text-muted-foreground">
								{app.description}
							</p>
						) : null}
						{app.privacy_url ? (
							<p className="mt-2 text-xs">
								<a
									href={app.privacy_url}
									target="_blank"
									rel="noreferrer"
									className="break-all text-primary underline-offset-4 hover:underline"
								>
									{app.privacy_url}
								</a>
							</p>
						) : null}
					</div>
				</AccordionTrigger>
				<AccordionContent className="px-6 pb-6 pt-2">
					<div className="flex flex-wrap items-center gap-2 pb-4">
						<Button
							size="sm"
							className="inline-flex items-center gap-2"
							onClick={(event) => {
								event.stopPropagation();
								onCreateKey(app);
							}}
						>
							<Plus size={14} />
							Create key
						</Button>
						<Button
							size="sm"
							variant="outline"
							className="inline-flex items-center gap-2"
							onClick={(event) => {
								event.stopPropagation();
								onEditApp(app);
							}}
						>
							<Pencil size={14} />
							Edit app
						</Button>
						<Button
							size="sm"
							variant="outline"
							className="inline-flex items-center gap-2 text-destructive"
							onClick={(event) => {
								event.stopPropagation();
								onDeleteApp(app);
							}}
						>
							<Trash2 size={14} />
							Delete app
						</Button>
						<Button
							size="icon"
							variant="ghost"
							onClick={(event) => {
								event.stopPropagation();
								onRefreshKeys(app);
							}}
							title="Refresh keys"
						>
							<RefreshCw size={16} />
						</Button>
					</div>

					{isLoadingKeys ? (
						<div className="space-y-3">
							{Array.from({ length: 2 }).map((_, index) => (
								<div
									key={`${app.app_id}-skeleton-${index}`}
									className="rounded-lg border border-dashed p-4"
								>
									<Skeleton className="h-5 w-40" />
									<div className="mt-3 flex flex-wrap gap-3">
										<Skeleton className="h-4 w-32" />
										<Skeleton className="h-4 w-24" />
									</div>
								</div>
							))}
						</div>
					) : keysError ? (
						<Alert variant="destructive">
							<AlertTitle>Failed to load keys</AlertTitle>
							<AlertDescription>{keysError}</AlertDescription>
						</Alert>
					) : keys.length === 0 ? (
						<div className="rounded-lg border border-dashed bg-muted/40 p-6 text-center">
							<p className="text-sm text-muted-foreground">
								No API keys yet. Create one to start using the API.
							</p>
							<Button
								variant="link"
								className="mt-2"
								onClick={(event) => {
									event.stopPropagation();
									onCreateKey(app);
								}}
							>
								Create your first key
							</Button>
						</div>
					) : (
						<div className="space-y-3">
							{keys.map((key) => (
								<div
									key={key.id}
									className="rounded-lg border bg-card/60 p-4 shadow-sm"
								>
									<div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
										<div>
											<p className="font-medium">{key.name || key.id}</p>
											<div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
												<div className="flex items-center gap-1">
													<span className="font-medium text-foreground">
														Created:
													</span>
													<span>{formatDate(key.created_at)}</span>
												</div>
												<div className="flex items-center gap-1">
													<span className="font-medium text-foreground">
														Expires:
													</span>
													<span>{formatDate(key.expires_at)}</span>
												</div>
											</div>
										</div>
										<div className="flex flex-wrap items-center gap-2">
											<Button
												variant="outline"
												size="sm"
												className="inline-flex items-center gap-2 text-destructive"
												onClick={(event) => {
													event.stopPropagation();
													onDeleteKey(app, key);
												}}
											>
												<Trash2 size={14} />
												Delete
											</Button>
										</div>
									</div>
								</div>
							))}
						</div>
					)}
				</AccordionContent>
			</Card>
		</AccordionItem>
	);
};
